import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { PrescriptionForm } from '@/components/prescriptions/PrescriptionForm'
import { ExternalPrescriptionForm } from '@/components/externalLabs/ExternalPrescriptionForm'
import { useAuth, usePermissions } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import { Loading } from '@/components/ui/loading'
import { ArrowLeft } from 'lucide-react'
import type { Id } from '../../../convex/_generated/dataModel'

export function NewPrescription() {
  const { profile } = useAuth()
  return <LaboratoryPrescriptionFlow key={`${profile?._id ?? ''}:${profile?.company_id || 'no-clinic'}`} />
}

function LaboratoryPrescriptionFlow() {
  const navigate = useNavigate()
  const { profile } = useAuth()
  const { hasPermission } = usePermissions()
  const canPrimoLab = hasPermission('create_prescription')
  const canExternal = hasPermission('create_external_prescription')
  const laboratories = useQuery(api.externalLabs.listLaboratories, canExternal && profile?.company_id ? {} : 'skip')
  const [choice, setChoice] = useState('')
  const [selected, setSelected] = useState('')
  const [selectedName, setSelectedName] = useState('')
  const [savingExternal, setSavingExternal] = useState(false)
  const externalLab = laboratories?.find(lab => lab._id === selected)
  const selectedStillAvailable = selected === 'primolab' ? canPrimoLab : Boolean(canExternal && externalLab)
  const unavailableReason = selected && selected !== 'primolab' && !selectedStillAvailable
    ? laboratories === undefined && canExternal
      ? 'Verifica della disponibilità del laboratorio in corso…'
      : 'Questo laboratorio non è più disponibile per la clinica attiva. I dati compilati restano visibili; per creare il lavoro scegli un laboratorio abilitato.'
    : undefined

  if (!profile) return <Loading />
  return (
    <div className="mx-auto max-w-[1400px] space-y-6">
      <div className="flex flex-col gap-3 border-b border-gray-200 pb-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-[-0.02em] text-gray-950">Nuova prescrizione</h1>
          <p className="mt-1 text-sm text-gray-600">{selected ? `Laboratorio: ${selected === 'primolab' ? 'PrimoLab' : externalLab?.name ?? selectedName}` : 'Scegli il laboratorio a cui affidare il lavoro.'}</p>
        </div>
        <Button variant="outline" size="sm" disabled={savingExternal} onClick={() => navigate('/prescriptions')} className="self-start sm:self-auto">
          <ArrowLeft className="mr-2 h-4 w-4" />Torna alle prescrizioni
        </Button>
      </div>
      {!selected || (selected === 'primolab' && !canPrimoLab) ? (
        <form className="max-w-2xl space-y-5 rounded-xl border border-gray-200 bg-white p-6" onSubmit={event => { event.preventDefault(); if (choice === 'primolab' ? canPrimoLab : laboratories?.some(lab => lab._id === choice)) { setSelectedName(laboratories?.find(lab => lab._id === choice)?.name ?? 'PrimoLab'); setSelected(choice) } }}>
          <div>
            <label htmlFor="prescription-laboratory" className="mb-2 block font-medium text-gray-900">Laboratorio <span className="font-normal text-gray-600">(obbligatorio)</span></label>
            <select id="prescription-laboratory" required value={choice} onChange={event => setChoice(event.target.value)} className="h-11 w-full rounded-md border border-input bg-white px-3 text-sm focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring">
              <option value="">Seleziona un laboratorio</option>
              {canPrimoLab && <option value="primolab">PrimoLab</option>}
              {laboratories?.map(lab => <option key={lab._id} value={lab._id}>{lab.name}</option>)}
            </select>
            <p className="mt-3 text-sm leading-6 text-gray-600">PrimoLab segue il percorso clinico guidato. Per gli altri laboratori puoi compilare liberamente il lavoro, la prova e le note, quindi scaricare il PDF.</p>
          </div>
          {canExternal && !profile.company_id && <p role="status" className="text-sm text-gray-700">Seleziona una clinica attiva per usare i laboratori esterni.</p>}
          {canExternal && profile.company_id && laboratories === undefined && <p role="status" className="text-sm text-gray-600">Caricamento laboratori…</p>}
          {canExternal && laboratories?.length === 0 && <p className="text-sm text-gray-600">Non ci sono laboratori esterni abilitati per questa clinica. {hasPermission('manage_external_laboratories') ? <Link className="font-medium text-gray-900 underline underline-offset-4" to="/listino?lab=external">Configura i laboratori della società nei listini</Link> : 'Chiedi al titolare o al personale di abilitare un laboratorio per questa clinica.'}</p>}
          <Button type="submit" variant="primoup" className="text-gray-950" disabled={!choice || (choice !== 'primolab' && !laboratories?.some(lab => lab._id === choice))}>Continua</Button>
        </form>
      ) : (
        <>
          <Button variant="outline" size="sm" disabled={savingExternal} onClick={() => { if (window.confirm('Cambiare laboratorio elimina i dati non ancora salvati di questa prescrizione. Continuare?')) { setSelected(''); setChoice('') } }}>Cambia laboratorio</Button>
          {selected === 'primolab' ? (
            <PrescriptionForm onSuccess={id => navigate(`/prescriptions/${id}`)} onCancel={() => navigate('/prescriptions')} />
          ) : (
            <ExternalPrescriptionForm laboratoryId={selected as Id<'external_laboratories'>} unavailableReason={unavailableReason} onSavingChange={setSavingExternal} onSuccess={id => navigate(`/prescriptions/external/${id}`)} onCancel={() => navigate('/prescriptions?lab=external')} />
          )}
        </>
      )}
    </div>
  )
}
